import { useTheme } from '../context/ThemeContext';
import { rewards, type ExpiryItem } from '../api/client';
import { useInfiniteList, rowCursor } from '../hooks/useInfiniteList';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** ISO timestamp → "30 November, 2027". */
function dateLabel(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
}

/**
 * "Expiring" statement — one row per active coin lot, by the exact date it
 * expires, soonest first, paginated on scroll (keyset, same pattern as the coin
 * Statement). Reused on the My ZS Coins page and the Rewards page. Pass
 * `resetKey` to force a reload (e.g. the user id).
 */
export default function CoinExpiryStatement({
  resetKey = 'expiry',
  maxHeight = 'max-h-80',
}: {
  resetKey?: unknown;
  maxHeight?: string;
}) {
  const { isDark } = useTheme();
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';

  const list = useInfiniteList<ExpiryItem>(
    async (cursor) => {
      const r = await rewards.expiry(cursor);
      return { items: r.items, hasMore: r.has_more };
    },
    (it) => rowCursor(it.expires_at, it.id),
    resetKey,
  );

  return (
    <div
      onScroll={list.onScroll}
      className={`rounded-xl border divide-y ${isDark ? 'border-gray-800 divide-gray-800' : 'border-gray-200 divide-gray-100'} ${maxHeight} overflow-y-auto`}
    >
      {list.items.length ? (
        list.items.map((it) => (
          <div key={it.id} className="flex items-center justify-between px-4 py-3 text-sm">
            <div>
              <p className={`text-xs ${sub}`}>Expiry</p>
              <p className="font-medium">{dateLabel(it.expires_at)}</p>
            </div>
            <span className="font-bold text-amber-500">{it.amount.toLocaleString('en-IN')}</span>
          </div>
        ))
      ) : (
        <p className={`px-4 py-6 text-center text-sm ${sub}`}>No coins scheduled to expire.</p>
      )}
      {list.loading && list.items.length > 0 && <p className={`px-4 py-3 text-center text-xs ${sub}`}>Loading…</p>}
    </div>
  );
}
